/**
 * @license
 * Copyright Google Inc. All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */


import {
  Component,
  ContentChildren,
  Directive,
  ElementRef,
  Renderer2,
  EventEmitter,
  Input,
  OnInit,
  OnDestroy,
  Optional,
  Output,
  QueryList,
  ViewChild,
  ViewEncapsulation,
  forwardRef,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
} from '@angular/core';
import {NG_VALUE_ACCESSOR, ControlValueAccessor} from '@angular/forms';
import {coerceBooleanProperty} from '@angular/cdk/coercion';
import {UniqueSelectionDispatcher} from '@angular/material/core';
import {CanDisable, mixinDisabled} from '@angular/material/core';
import {FocusMonitor} from '@angular/cdk/a11y';

/** Acceptable types for a button toggle. */
export type ToggleType = 'checkbox' | 'radio';
/**
 * \@docs-private
 */
export class MdButtonToggleGroupBase {}
export const /** @type {?} */ _MdButtonToggleGroupMixinBase = mixinDisabled(MdButtonToggleGroupBase);
/**
 * Provider Expression that allows md-button-toggle-group to register as a ControlValueAccessor.
 * This allows it to support [(ngModel)].
 * \@docs-private
 */
export const MD_BUTTON_TOGGLE_GROUP_VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => MdButtonToggleGroup),
  multi: true
};

let /** @type {?} */ _uniqueIdCounter = 0;
/**
 * Change event object emitted by MdButtonToggle.
 */
export class MdButtonToggleChange {
/**
 * The MdButtonToggle that emits the event.
 */
source: MdButtonToggle | null;
/**
 * The value assigned to the MdButtonToggle.
 */
value: any;
}

function MdButtonToggleChange_tsickle_Closure_declarations() {
/**
 * The MdButtonToggle that emits the event.
 * @type {?}
 */
MdButtonToggleChange.prototype.source;
/**
 * The value assigned to the MdButtonToggle.
 * @type {?}
 */
MdButtonToggleChange.prototype.value;
}

/**
 * Exclusive selection button toggle group that behaves like a radio-button group.
 */
export class MdButtonToggleGroup extends _MdButtonToggleGroupMixinBase
    implements ControlValueAccessor, CanDisable {
/**
 * The value for the button toggle group. Should match currently selected button toggle.
 */
private _value: any = null;
/**
 * The HTML name attribute applied to toggles in this group.
 */
private _name: string = `md-button-toggle-group-${_uniqueIdCounter++}`;
/**
 * Whether the button toggle group should be vertical.
 */
private _vertical: boolean = false;
/**
 * The currently selected button toggle, should match the value.
 */
private _selected: MdButtonToggle | null = null;
/**
 * The method to be called in order to update ngModel.
 * Now `ngModel` binding is not supported in multiple selection mode.
 */
_controlValueAccessorChangeFn: (value: any) => void = () => {};
/**
 * onTouch function registered via registerOnTouch (ControlValueAccessor).
 */
onTouched: () => any = () => {};
/**
 * Child button toggle buttons.
 */
_buttonToggles: QueryList<MdButtonToggle>;
/**
 * `name` attribute for the underlying `input` element.
 * @return {?}
 */
get name(): string {
    return this._name;
  }
/**
 * @param {?} value
 * @return {?}
 */
set name(value: string) {
    this._name = value;
    this._updateButtonToggleNames();
  }
/**
 * Whether the toggle group is vertical.
 * @return {?}
 */
get vertical(): boolean {
    return this._vertical;
  }
/**
 * @param {?} value
 * @return {?}
 */
set vertical(value) {
    this._vertical = coerceBooleanProperty(value);
  }
/**
 * Value of the toggle group.
 * @return {?}
 */
get value(): any {
    return this._value;
  }
/**
 * @param {?} newValue
 * @return {?}
 */
set value(newValue: any) {
    if (this._value != newValue) {
      this._value = newValue;

      this._updateSelectedButtonToggleFromValue();
    }
  }
/**
 * Whether the toggle group is selected.
 * @return {?}
 */
get selected() {
    return this._selected;
  }
/**
 * @param {?} selected
 * @return {?}
 */
set selected(selected: MdButtonToggle | null) {
    this._selected = selected;
    this.value = selected ? selected.value : null;

    if (selected && !selected.checked) {
      selected.checked = true;
    }
  }
/**
 * Event emitted when the group's value changes.
 */
change: EventEmitter<MdButtonToggleChange> = new EventEmitter<MdButtonToggleChange>();
/**
 * @param {?} _changeDetector
 */
constructor(private _changeDetector: ChangeDetectorRef) {
    super();
  }
/**
 * @return {?}
 */
private _updateButtonToggleNames(): void {
    if (this._buttonToggles) {
      this._buttonToggles.forEach((toggle) => {
        toggle.name = this._name;
      });
    }
  }
/**
 * @return {?}
 */
private _updateSelectedButtonToggleFromValue(): void {
    let /** @type {?} */ isAlreadySelected = this._selected != null && this._selected.value == this._value;

    if (this._buttonToggles != null && !isAlreadySelected) {
      let /** @type {?} */ matchingButtonToggle = this._buttonToggles.filter(
          buttonToggle => buttonToggle.value == this._value)[0];

      if (matchingButtonToggle) {
        this.selected = matchingButtonToggle;
      } else if (this.value == null) {
        this.selected = null;
        this._buttonToggles.forEach(buttonToggle => {
          buttonToggle.checked = false;
        });
      }
    }
  }
/**
 * Dispatch change event with current selection and group value.
 * @return {?}
 */
_emitChangeEvent(): void {
    let /** @type {?} */ event = new MdButtonToggleChange();
    event.source = this._selected;
    event.value = this._value;
    this._controlValueAccessorChangeFn(event.value);
    this.change.emit(event);
  }
/**
 * Sets the model value. Implemented as part of ControlValueAccessor.
 * @param {?} value Value to be set to the model.
 * @return {?}
 */
writeValue(value: any) {
    this.value = value;
    this._changeDetector.markForCheck();
  }
/**
 * Registers a callback that will be triggered when the value has changed.
 * Implemented as part of ControlValueAccessor.
 * @param {?} fn On change callback function.
 * @return {?}
 */
registerOnChange(fn: (value: any) => void) {
    this._controlValueAccessorChangeFn = fn;
  }
/**
 * Registers a callback that will be triggered when the control has been touched.
 * Implemented as part of ControlValueAccessor.
 * @param {?} fn On touch callback function.
 * @return {?}
 */
registerOnTouched(fn: any) {
    this.onTouched = fn;
  }
/**
 * Toggles the disabled state of the component. Implemented as part of ControlValueAccessor.
 * @param {?} isDisabled Whether the component should be disabled.
 * @return {?}
 */
setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }
static decorators: DecoratorInvocation[] = [
{ type: Directive, args: [{
  selector: 'md-button-toggle-group:not([multiple]), mat-button-toggle-group:not([multiple])',
  providers: [MD_BUTTON_TOGGLE_GROUP_VALUE_ACCESSOR],
  inputs: ['disabled'],
  host: {
    'role': 'radiogroup',
    'class': 'mat-button-toggle-group',
    '[class.mat-button-toggle-vertical]': 'vertical'
  },
  exportAs: 'mdButtonToggleGroup, matButtonToggleGroup',
}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: ChangeDetectorRef, },
];
static propDecorators: {[key: string]: DecoratorInvocation[]} = {
'_buttonToggles': [{ type: ContentChildren, args: [forwardRef(() => MdButtonToggle), ] },],
'name': [{ type: Input },],
'vertical': [{ type: Input },],
'value': [{ type: Input },],
'selected': [{ type: Input },],
'change': [{ type: Output },],
};
}

function MdButtonToggleGroup_tsickle_Closure_declarations() {
/** @type {?} */
MdButtonToggleGroup.decorators;
/**
 * @nocollapse
 * @type {?}
 */
MdButtonToggleGroup.ctorParameters;
/** @type {?} */
MdButtonToggleGroup.propDecorators;
/**
 * The value for the button toggle group. Should match currently selected button toggle.
 * @type {?}
 */
MdButtonToggleGroup.prototype._value;
/**
 * The HTML name attribute applied to toggles in this group.
 * @type {?}
 */
MdButtonToggleGroup.prototype._name;
/**
 * Whether the button toggle group should be vertical.
 * @type {?}
 */
MdButtonToggleGroup.prototype._vertical;
/**
 * The currently selected button toggle, should match the value.
 * @type {?}
 */
MdButtonToggleGroup.prototype._selected;
/**
 * The method to be called in order to update ngModel.
 * Now `ngModel` binding is not supported in multiple selection mode.
 * @type {?}
 */
MdButtonToggleGroup.prototype._controlValueAccessorChangeFn;
/**
 * onTouch function registered via registerOnTouch (ControlValueAccessor).
 * @type {?}
 */
MdButtonToggleGroup.prototype.onTouched;
/**
 * Child button toggle buttons.
 * @type {?}
 */
MdButtonToggleGroup.prototype._buttonToggles;
/**
 * Event emitted when the group's value changes.
 * @type {?}
 */
MdButtonToggleGroup.prototype.change;
/** @type {?} */
MdButtonToggleGroup.prototype._changeDetector;
}

/**
 * Multiple selection button-toggle group. `ngModel` is not supported in this mode.
 */
export class MdButtonToggleGroupMultiple extends _MdButtonToggleGroupMixinBase
    implements CanDisable {
/**
 * Whether the button toggle group should be vertical.
 */
private _vertical: boolean = false;
/**
 * Whether the toggle group is vertical.
 * @return {?}
 */
get vertical(): boolean {
    return this._vertical;
  }
/**
 * @param {?} value
 * @return {?}
 */
set vertical(value) {
    this._vertical = coerceBooleanProperty(value);
  }
static decorators: DecoratorInvocation[] = [
{ type: Directive, args: [{
  selector: 'md-button-toggle-group[multiple], mat-button-toggle-group[multiple]',
  exportAs: 'mdButtonToggleGroup, matButtonToggleGroup',
  inputs: ['disabled'],
  host: {
    'class': 'mat-button-toggle-group',
    '[class.mat-button-toggle-vertical]': 'vertical',
    'role': 'group'
  }
}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
];
static propDecorators: {[key: string]: DecoratorInvocation[]} = {
'vertical': [{ type: Input },],
};
}

function MdButtonToggleGroupMultiple_tsickle_Closure_declarations() {
/** @type {?} */
MdButtonToggleGroupMultiple.decorators;
/**
 * @nocollapse
 * @type {?}
 */
MdButtonToggleGroupMultiple.ctorParameters;
/** @type {?} */
MdButtonToggleGroupMultiple.propDecorators;
/**
 * Whether the button toggle group should be vertical.
 * @type {?}
 */
MdButtonToggleGroupMultiple.prototype._vertical;
}

/**
 * Single button inside of a toggle group.
 */
export class MdButtonToggle implements OnInit, OnDestroy {
/**
 * Attached to the aria-label attribute of the host element. In most cases, arial-labelledby will
 * take precedence so this may be omitted.
 */
ariaLabel: string = '';
/**
 * Users can specify the `aria-labelledby` attribute which will be forwarded to the input element
 */
ariaLabelledby: string | null = null;
/**
 * Whether or not this button toggle is checked.
 */
private _checked: boolean = false;
/**
 * Type of the button toggle. Either 'radio' or 'checkbox'.
 */
_type: ToggleType;
/**
 * Whether or not this button toggle is disabled.
 */
private _disabled: boolean = false;
/**
 * Value assigned to this button toggle.
 */
private _value: any = null;
/**
 * Whether or not the button toggle is a single selection.
 */
private _isSingleSelector: boolean = false;
/**
 * Unregister function for _buttonToggleDispatcher *
 */
private _removeUniqueSelectionListener: () => void = () => {};

   _inputElement: ElementRef;
/**
 * The parent button toggle group (exclusive selection). Optional.
 */
buttonToggleGroup: MdButtonToggleGroup;
/**
 * The parent button toggle group (multiple selection). Optional.
 */
buttonToggleGroupMultiple: MdButtonToggleGroupMultiple;
/**
 * Unique ID for the underlying `input` element.
 * @return {?}
 */
get inputId(): string {
    return `${this.id}-input`;
  }
/**
 * The unique ID for this button toggle.
 */
id: string;
/**
 * HTML's 'name' attribute used to group radios for unique selection.
 */
name: string;
/**
 * Whether the button is checked.
 * @return {?}
 */
get checked(): boolean { return this._checked; }
/**
 * @param {?} newCheckedState
 * @return {?}
 */
set checked(newCheckedState: boolean) {
    if (this._isSingleSelector && newCheckedState) {
      // Notify all button toggles with the same name (in the same group) to un-check.
      this._buttonToggleDispatcher.notify(this.id, this.name);
      this._changeDetectorRef.markForCheck();
    }

    this._checked = newCheckedState;

    if (newCheckedState && this._isSingleSelector && this.buttonToggleGroup.value != this.value) {
      this.buttonToggleGroup.selected = this;
    }
  }
/**
 * MdButtonToggleGroup reads this to assign its own value.
 * @return {?}
 */
get value(): any {
    return this._value;
  }
/**
 * @param {?} value
 * @return {?}
 */
set value(value: any) {
    if (this._value != value) {
      if (this.buttonToggleGroup != null && this.checked) {
        this.buttonToggleGroup.value = value;
      }
      this._value = value;
    }
  }
/**
 * Whether the button is disabled.
 * @return {?}
 */
get disabled(): boolean {
    return this._disabled || (this.buttonToggleGroup != null && this.buttonToggleGroup.disabled) ||
        (this.buttonToggleGroupMultiple != null && this.buttonToggleGroupMultiple.disabled);
  }
/**
 * @param {?} value
 * @return {?}
 */
set disabled(value: boolean) {
    this._disabled = coerceBooleanProperty(value);
  }
/**
 * Event emitted when the group value changes.
 */
change: EventEmitter<MdButtonToggleChange> = new EventEmitter<MdButtonToggleChange>();
/**
 * @param {?} toggleGroup
 * @param {?} toggleGroupMultiple
 * @param {?} _changeDetectorRef
 * @param {?} _buttonToggleDispatcher
 * @param {?} _renderer
 * @param {?} _elementRef
 * @param {?} _focusMonitor
 */
constructor( toggleGroup: MdButtonToggleGroup,
               toggleGroupMultiple: MdButtonToggleGroupMultiple,
private _changeDetectorRef: ChangeDetectorRef,
private _buttonToggleDispatcher: UniqueSelectionDispatcher,
private _renderer: Renderer2,
private _elementRef: ElementRef,
private _focusMonitor: FocusMonitor) {

    this.buttonToggleGroup = toggleGroup;
    this.buttonToggleGroupMultiple = toggleGroupMultiple;

    if (this.buttonToggleGroup) {
      this._removeUniqueSelectionListener =
        _buttonToggleDispatcher.listen((id: string, name: string) => {
          if (id != this.id && name == this.name) {
            this.checked = false;
            this._changeDetectorRef.markForCheck();
          }
        });

      this._type = 'radio';
      this.name = this.buttonToggleGroup.name;
      this._isSingleSelector = true;
    } else {
      // Even if there is no group at all, treat the button toggle as a checkbox so it can be
      // toggled on or off.
      this._type = 'checkbox';
      this._isSingleSelector = false;
    }
  }
/**
 * @return {?}
 */
ngOnInit() {
    if (this.id == null) {
      this.id = `md-button-toggle-${_uniqueIdCounter++}`;
    }

    if (this.buttonToggleGroup && this._value == this.buttonToggleGroup.value) {
      this._checked = true;
    }
    this._focusMonitor.monitor(this._elementRef.nativeElement, this._renderer, true);
  }
/**
 * Focuses the button.
 * @return {?}
 */
focus() {
    this._inputElement.nativeElement.focus();
  }
/**
 * Toggle the state of the current button toggle.
 * @return {?}
 */
private _toggle(): void {
    this.checked = !this.checked;
  }
/**
 * Checks the button toggle due to an interaction with the underlying native input.
 * @param {?} event
 * @return {?}
 */
_onInputChange(event: Event) {
    event.stopPropagation();

    if (this._isSingleSelector) {
      // Propagate the change one-way via the group, which will in turn mark this
      // button toggle as checked.
      let /** @type {?} */ groupValueChanged = this.buttonToggleGroup.selected != this;
      this.checked = true;
      this.buttonToggleGroup.selected = this;
      this.buttonToggleGroup.onTouched();
      if (groupValueChanged) {
        this.buttonToggleGroup._emitChangeEvent();
      }
    } else {
      this._toggle();
    }

    // Emit a change event when the native input does.
    this._emitChangeEvent();
  }
/**
 * @param {?} event
 * @return {?}
 */
_onInputClick(event: Event) {
    // We have to stop propagation for click events on the visual hidden input element.
    // By default, when a user clicks on a label element, a generated click event will be
    // dispatched on the associated input element. Since we are using a label element as our
    // root container, the click event on the `slide-toggle` will be executed twice.
    // The real click event will bubble up, and the generated click event also tries to bubble up.
    // This will lead to multiple click events.
    // Preventing bubbling for the second event will solve that issue.
    event.stopPropagation();
  }
/**
 * Dispatch change event with current value.
 * @return {?}
 */
private _emitChangeEvent(): void {
    let /** @type {?} */ event = new MdButtonToggleChange();
    event.source = this;
    event.value = this._value;
    this.change.emit(event);
  }
/**
 * @return {?}
 */
ngOnDestroy(): void {
    this._removeUniqueSelectionListener();
  }
static decorators: DecoratorInvocation[] = [
{ type: Component, args: [{
  moduleId: module.id,
  selector: 'md-button-toggle, mat-button-toggle',
  templateUrl: 'button-toggle.html',
  styleUrls: ['button-toggle.css'],
  encapsulation: ViewEncapsulation.None,
  preserveWhitespaces: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.mat-button-toggle-standalone]': '!buttonToggleGroup && !buttonToggleGroupMultiple',
    '[class.mat-button-toggle-checked]': 'checked',
    '[class.mat-button-toggle-disabled]': 'disabled',
    'class': 'mat-button-toggle',
    '[attr.id]': 'id',
  }
}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: MdButtonToggleGroup, decorators: [{ type: Optional }, ]},
{type: MdButtonToggleGroupMultiple, decorators: [{ type: Optional }, ]},
{type: ChangeDetectorRef, },
{type: UniqueSelectionDispatcher, },
{type: Renderer2, },
{type: ElementRef, },
{type: FocusMonitor, },
];
static propDecorators: {[key: string]: DecoratorInvocation[]} = {
'ariaLabel': [{ type: Input, args: ['aria-label', ] },],
'ariaLabelledby': [{ type: Input, args: ['aria-labelledby', ] },],
'_inputElement': [{ type: ViewChild, args: ['input', ] },],
'id': [{ type: Input },],
'name': [{ type: Input },],
'checked': [{ type: Input },],
'value': [{ type: Input },],
'disabled': [{ type: Input },],
'change': [{ type: Output },],
};
}

function MdButtonToggle_tsickle_Closure_declarations() {
/** @type {?} */
MdButtonToggle.decorators;
/**
 * @nocollapse
 * @type {?}
 */
MdButtonToggle.ctorParameters;
/** @type {?} */
MdButtonToggle.propDecorators;
/**
 * Attached to the aria-label attribute of the host element. In most cases, arial-labelledby will
 * take precedence so this may be omitted.
 * @type {?}
 */
MdButtonToggle.prototype.ariaLabel;
/**
 * Users can specify the `aria-labelledby` attribute which will be forwarded to the input element
 * @type {?}
 */
MdButtonToggle.prototype.ariaLabelledby;
/**
 * Whether or not this button toggle is checked.
 * @type {?}
 */
MdButtonToggle.prototype._checked;
/**
 * Type of the button toggle. Either 'radio' or 'checkbox'.
 * @type {?}
 */
MdButtonToggle.prototype._type;
/**
 * Whether or not this button toggle is disabled.
 * @type {?}
 */
MdButtonToggle.prototype._disabled;
/**
 * Value assigned to this button toggle.
 * @type {?}
 */
MdButtonToggle.prototype._value;
/**
 * Whether or not the button toggle is a single selection.
 * @type {?}
 */
MdButtonToggle.prototype._isSingleSelector;
/**
 * Unregister function for _buttonToggleDispatcher *
 * @type {?}
 */
MdButtonToggle.prototype._removeUniqueSelectionListener;
/** @type {?} */
MdButtonToggle.prototype._inputElement;
/**
 * The parent button toggle group (exclusive selection). Optional.
 * @type {?}
 */
MdButtonToggle.prototype.buttonToggleGroup;
/**
 * The parent button toggle group (multiple selection). Optional.
 * @type {?}
 */
MdButtonToggle.prototype.buttonToggleGroupMultiple;
/**
 * The unique ID for this button toggle.
 * @type {?}
 */
MdButtonToggle.prototype.id;
/**
 * HTML's 'name' attribute used to group radios for unique selection.
 * @type {?}
 */
MdButtonToggle.prototype.name;
/**
 * Event emitted when the group value changes.
 * @type {?}
 */
MdButtonToggle.prototype.change;
/** @type {?} */
MdButtonToggle.prototype._changeDetectorRef;
/** @type {?} */
MdButtonToggle.prototype._buttonToggleDispatcher;
/** @type {?} */
MdButtonToggle.prototype._renderer;
/** @type {?} */
MdButtonToggle.prototype._elementRef;
/** @type {?} */
MdButtonToggle.prototype._focusMonitor;
}


interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
